import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, getPostAuthDestination, normalizeAuthRedirect } from "@/context/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { auth } from "@/lib/firebase";
import { signInWithPopup, GoogleAuthProvider, FacebookAuthProvider } from "firebase/auth";
import { toast } from "sonner";

const PLAYER_HERO_IMAGE =
  "https://images.unsplash.com/photo-1561554078-b9a19c6511d9?w=1000&crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxwYWRlbCUyMGNvdXJ0fGVufDB8fHx8MTc3ODYyMTA5NHww&ixlib=rb-4.1.0&q=85";

const HOST_HERO_IMAGE = "https://images.unsplash.com/photo-1646643508957-088fe0eac722?w=1000&crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxwYWRlbCUyMGNvdXJ0fGVufDB8fHx8MTc3ODYyMTA5NHww&ixlib=rb-4.1.0&q=85";
//   "https://coresg-normal.trae.ai/api/ide/v1/text_to_image?prompt=realistic%20sports%20facility%20owner%20reviewing%20bookings%20on%20a%20tablet%20beside%20modern%20padel%20courts%2C%20golden%20hour%2C%20professional%20hospitality%20business%20atmosphere%2C%20clean%20premium%20composition%2C%20photorealistic%2C%20wide%20hero%20shot%2C%20no%20text%2C%20no%20watermark&image_size=landscape_16_9";

function getRegisterErrorMessage(t, err) {
  const code = err?.code;
  const message = err?.message || "";
  const detail = formatApiError(err?.response?.data?.detail);
  const combinedMessage = `${detail} ${message}`.toLowerCase();

  if (code === "auth/email-already-in-use" || combinedMessage.includes("already") || combinedMessage.includes("exists")) {
    return t("register.email_in_use");
  }
  if (code === "auth/popup-closed-by-user") {
    return t("register.popup_closed");
  }
  if (code === "auth/too-many-requests") {
    return t("auth.too_many_requests");
  }
  return detail || message || t("common.registration_failed");
}

function validateRegisterForm(form, t) {
  const errors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!form.name.trim()) {
    errors.name = t("register.validation_name_required");
  } else if (form.name.trim().length < 2) {
    errors.name = t("register.validation_name_short");
  }

  if (!form.email.trim()) {
    errors.email = t("register.validation_email_required");
  } else if (!emailPattern.test(form.email.trim())) {
    errors.email = t("register.validation_email_invalid");
  }

  if (!form.password) {
    errors.password = t("register.validation_password_required");
  } else if (form.password.length < 8) {
    errors.password = t("register.validation_password_short");
  } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(form.password)) {
    errors.password = t("register.validation_password_weak");
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = t("register.validation_confirm_password_required");
  } else if (form.confirmPassword !== form.password) {
    errors.confirmPassword = t("register.validation_password_mismatch");
  }

  return errors;
}

function getRegistrationCopy(variant) {
  if (variant === "host") {
    return {
      defaultRole: "owner",
      namespace: "host_register",
      heroImage: HOST_HERO_IMAGE,
      heroEyebrow: "host_register.hero_eyebrow",
      heroTitle: "host_register.hero_title",
      switchPrompt: "host_register.player_prompt",
      switchAction: "host_register.player_link",
      switchPath: "/register",
    };
  }

  return {
    defaultRole: "player",
    namespace: "register",
    heroImage: PLAYER_HERO_IMAGE,
    heroEyebrow: "register.its_free",
    heroTitle: "register.games_start",
    switchPrompt: "register.host_prompt",
    switchAction: "register.host_link",
    switchPath: "/register/host",
  };
}

export default function AuthRegistrationPage({ variant = "player" }) {
  const { t } = useTranslation();
  const { register, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = normalizeAuthRedirect(params.get("redirect"));
  const copy = useMemo(() => getRegistrationCopy(variant), [variant]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [socialProvider, setSocialProvider] = useState("");
  const [progressKey, setProgressKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const errors = validateRegisterForm(form, t);
  const isBusy = submitting || Boolean(socialProvider);
  const loginLink = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login";
  const switchLink = redirect
    ? `${copy.switchPath}?redirect=${encodeURIComponent(redirect)}`
    : copy.switchPath;
  const testIdPrefix = variant === "player" ? "register" : "host-register";

  const set = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const touch = (field) => () => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const fieldError = (field) => (touched[field] || submitAttempted ? errors[field] : "");

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitAttempted(true);
    setError("");
    if (Object.keys(errors).length > 0) {
      setError(t("register.fix_validation_errors"));
      return;
    }

    setSubmitting(true);
    setProgressKey("register.creating_account");
    try {
      const user = await register(
        {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          role: copy.defaultRole,
        },
        {
          onStatusChange: (status) => {
            setProgressKey(status === "signingYouIn" ? "register.signing_you_in" : "register.creating_account");
          },
        },
      );
      toast.success(t("common.account_created"));
      if (variant === "host") {
        navigate("/verify-phone?redirect=/owner", { replace: true });
      } else {
        navigate(getPostAuthDestination(user, redirect), { replace: true });
      }
    } catch (err) {
      setError(getRegisterErrorMessage(t, err));
    } finally {
      setSubmitting(false);
      setProgressKey("");
    }
  };

  const socialSignUp = async (providerName, provider) => {
    setError("");
    setSocialProvider(providerName);
    try {
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      const { data } = await api.post("/auth/oauth-callback", {
        id_token: idToken,
        role: copy.defaultRole,
      });
      if (data.token) localStorage.setItem("cb_token", data.token);
      if (data.user) {
        localStorage.setItem("cb_user", JSON.stringify(data.user));
        setUser(data.user);
      }
      toast.success(t("common.account_created"));
      if (variant === "host") {
        navigate("/verify-phone?redirect=/owner", { replace: true });
      } else {
        navigate(getPostAuthDestination(data.user, redirect), { replace: true });
      }
    } catch (err) {
      setError(getRegisterErrorMessage(t, err));
    } finally {
      setSocialProvider("");
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background grid md:grid-cols-2">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1">
        <div className="w-full max-w-md">
          <div className="label-eyebrow text-volt mb-3">{t(`${copy.namespace}.signup_eyebrow`)}</div>
          <h1 className="font-heading text-5xl mb-8 tracking-tight">{t(`${copy.namespace}.title`)}</h1>

          {redirect && (
            <div className="mb-5 rounded-sm border border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground">
              {t(`${copy.namespace}.redirect_notice`)}
            </div>
          )}

          <div className="flex flex-col gap-3 mb-5">
            <button
              type="button"
              onClick={() => socialSignUp("google", new GoogleAuthProvider())}
              data-testid={`${testIdPrefix}-google-btn`}
              disabled={isBusy}
              className="w-full btn-outline rounded-sm py-3 text-xs flex items-center justify-center gap-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
                <path d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              {socialProvider === "google"
                ? t(`${copy.namespace}.continue_google_loading`)
                : t(`${copy.namespace}.continue_google`)}
            </button>

            <button
              type="button"
              onClick={() => socialSignUp("facebook", new FacebookAuthProvider())}
              data-testid={`${testIdPrefix}-facebook-btn`}
              disabled={isBusy}
              className="w-full btn-outline rounded-sm py-3 text-xs flex items-center justify-center gap-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {socialProvider === "facebook"
                ? t(`${copy.namespace}.continue_facebook_loading`)
                : t(`${copy.namespace}.continue_facebook`)}
            </button>
          </div>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border/30" />
            <span className="text-[10px] tracking-widest text-muted-foreground/60 uppercase">{t("register.or_email")}</span>
            <div className="flex-1 h-px bg-border/30" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">{t("register.name")}</label>
              <input
                data-testid={`${testIdPrefix}-name-input`}
                required
                value={form.name}
                onChange={set("name")}
                onBlur={touch("name")}
                className="w-full bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none"
              />
              {fieldError("name") && <p className="mt-2 text-sm text-red-200">{fieldError("name")}</p>}
            </div>
            <div>
              <label className="label-eyebrow block mb-2">{t("register.email")}</label>
              <input
                data-testid={`${testIdPrefix}-email-input`}
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                onBlur={touch("email")}
                className="w-full bg-card border border-border px-4 py-3 focus:border-volt focus:outline-none"
              />
              {fieldError("email") && <p className="mt-2 text-sm text-red-200">{fieldError("email")}</p>}
            </div>
            <div>
              <label className="label-eyebrow block mb-2">{t("register.password_hint")}</label>
              <div className="relative">
                <input
                  data-testid={`${testIdPrefix}-password-input`}
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={form.password}
                  onChange={set("password")}
                  onBlur={touch("password")}
                  className="w-full bg-card border border-border px-4 py-3 pe-12 focus:border-volt focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldError("password") && <p className="mt-2 text-sm text-red-200">{fieldError("password")}</p>}
            </div>
            <div>
              <label className="label-eyebrow block mb-2">{t("register.confirm_password")}</label>
              <div className="relative">
                <input
                  data-testid={`${testIdPrefix}-confirm-password-input`}
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={form.confirmPassword}
                  onChange={set("confirmPassword")}
                  onBlur={touch("confirmPassword")}
                  className="w-full bg-card border border-border px-4 py-3 pe-12 focus:border-volt focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showConfirmPassword ? t("auth.hide_password") : t("auth.show_password")}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {fieldError("confirmPassword") && (
                <p className="mt-2 text-sm text-red-200">{fieldError("confirmPassword")}</p>
              )}
            </div>
            {error && (
              <div
                data-testid={`${testIdPrefix}-error`}
                className="rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              >
                <p className="font-medium">{t("register.error_title")}</p>
                <p className="mt-1 text-red-100/80">{error}</p>
              </div>
            )}
            <button
              data-testid={`${testIdPrefix}-submit-btn`}
              disabled={isBusy}
              className="btn-volt w-full py-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t(progressKey || "register.creating_account") : t(`${copy.namespace}.create_account`)}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("register.have_account")}{" "}
            <Link to={loginLink} className="text-volt underline-offset-4 hover:underline" data-testid={`${testIdPrefix === "register" ? "login" : testIdPrefix}-link`}>
              {t("register.sign_in")}
            </Link>
          </p>

          <div className="mt-4 rounded-sm border border-dashed border-border bg-card/40 p-4 text-sm text-muted-foreground">
            <p>
              {t(copy.switchPrompt)}{" "}
              <Link to={switchLink} className="text-volt underline-offset-4 hover:underline">
                {t(copy.switchAction)}
              </Link>
            </p>
          </div>
        </div>
      </div>
      <div className="relative order-3 md:order-2 min-h-[360px] md:min-h-full">
        <img src={copy.heroImage} className="absolute inset-0 h-full w-full object-cover grayscale-[40%]" alt="" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20 md:bg-gradient-to-bl md:from-background md:via-background/40 md:to-transparent" />
        <div className="absolute inset-0 flex flex-col p-6 md:p-10">
          <div className="mx-auto w-full max-w-md md:mx-0">
            <div className="label-eyebrow !text-white mb-3">{t(copy.heroEyebrow)}</div>
            <h2 className="font-heading text-3xl leading-[0.95] md:text-5xl md:leading-[0.9]">{t(copy.heroTitle)}</h2>
            <div className="mt-6 rounded-sm border border-border/70 bg-background/75 p-4 backdrop-blur-sm md:bg-card/70">
              <div className="label-eyebrow !text-white mb-3">{t(`${copy.namespace}.benefits_eyebrow`)}</div>
              <div className="space-y-2 text-sm text-white">
                {[1, 2, 3, 4]
                  .filter((index) => t(`${copy.namespace}.benefit_${index}`) !== `${copy.namespace}.benefit_${index}`)
                  .map((index) => (
                    <p key={index}>{t(`${copy.namespace}.benefit_${index}`)}</p>
                  ))}
              </div>
              <p className="mt-4 text-sm text-white/90">{t(`${copy.namespace}.reassurance_copy`)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
